/**
 * Accommodation Selector Component
 * Allows clients to select accommodations for their custom safari locations
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { accommodationService } from '../../services/accommodationService';
import { AccommodationProvider, AccommodationSelection } from '../../types/accommodation';
import { Tour } from '../../types';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface AccommodationSelectorProps {
  tours: Tour[];
  selections: AccommodationSelection[];
  onSelectionsChange: (selections: AccommodationSelection[]) => void;
  currency: string;
}

const AccommodationSelector: React.FC<AccommodationSelectorProps> = ({
  tours,
  selections,
  onSelectionsChange,
  currency
}) => {
  const { actualTheme } = useTheme();
  const [providersByTour, setProvidersByTour] = useState<Record<string, AccommodationProvider[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadProvidersForTours();
  }, [tours]);

  const loadProvidersForTours = async () => {
    const providersMap: Record<string, AccommodationProvider[]> = {};
    const loadingMap: Record<string, boolean> = {};

    for (const tour of tours) {
      loadingMap[tour.id] = true;
      try {
        const providers = await accommodationService.getProvidersByTour(tour.id);
        providersMap[tour.id] = providers;
      } catch (error) {
        console.error(`Error loading providers for tour ${tour.id}:`, error);
        providersMap[tour.id] = [];
      } finally {
        loadingMap[tour.id] = false;
      }
    }

    setProvidersByTour(providersMap);
    setLoading(loadingMap);
  };

  const getLocationKey = (tourId: string, location: string) => `${tourId}_${location}`;

  const getSelectionForLocation = (tourId: string, location: string): AccommodationSelection | undefined => {
    return selections.find(s => s.tourId === tourId && s.location === location);
  };

  const updateSelection = (tourId: string, location: string, updates: Partial<AccommodationSelection>) => {
    const existing = getSelectionForLocation(tourId, location);
    const newSelections = [...selections];

    if (existing) {
      const index = newSelections.indexOf(existing);
      newSelections[index] = { ...existing, ...updates };
    } else {
      newSelections.push({
        id: `temp-${Date.now()}`,
        bookingRequestId: '',
        providerId: updates.providerId || '',
        tourId,
        location,
        roomType: updates.roomType || 'standard',
        roomCount: updates.roomCount || 1,
        guestCount: updates.guestCount || 1,
        pricePerNight: updates.pricePerNight || 0,
        totalPrice: updates.totalPrice || 0,
        currency: updates.currency || 'USD',
        checkInDate: updates.checkInDate || '',
        checkOutDate: updates.checkOutDate || '',
        nights: updates.nights || 1,
        status: 'selected' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...updates
      });
    }

    onSelectionsChange(newSelections);
  };

  const removeSelection = (tourId: string, location: string) => {
    onSelectionsChange(selections.filter(s => !(s.tourId === tourId && s.location === location)));
  };

  const toggleLocation = (tourId: string, location: string) => {
    const key = getLocationKey(tourId, location);
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedLocations(newExpanded);
  };

  const getTypeColor = (type: AccommodationProvider['type']) => {
    const colors: Record<string, string> = {
      hotel: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      lodge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      camp: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      resort: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      restaurant: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      guesthouse: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      other: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
    };
    return colors[type] || colors.other;
  };

  const calculatePrice = (provider: AccommodationProvider, nights: number, mealPlan?: string): number => {
    let price = (provider.basePricePerNight || 0) * nights;
    
    if (mealPlan && provider.mealPlans) {
      const mealPlanPrice = provider.mealPlans[mealPlan as keyof typeof provider.mealPlans];
      if (mealPlanPrice) {
        price += mealPlanPrice * nights;
      }
    }

    return price;
  };

  // Group tours by location
  const locationsMap = new Map<string, { tour: Tour; providers: AccommodationProvider[] }>();
  
  tours.forEach(tour => {
    const location = tour.location?.name || 'Unknown Location';
    const key = `${tour.id}_${location}`;
    if (!locationsMap.has(key)) {
      locationsMap.set(key, {
        tour,
        providers: providersByTour[tour.id] || []
      });
    }
  });

  if (locationsMap.size === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-3 rounded-lg ${
          actualTheme === 'dark' ? 'bg-primary-700/20' : 'bg-primary-100'
        }`}>
          <BuildingOfficeIcon className={`w-6 h-6 ${
            actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
          }`} />
        </div>
        <div>
          <h3 className={`text-xl font-bold transition-colors duration-300 ${
            actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
          }`}>
            Select Accommodations
          </h3>
          <p className={`text-sm transition-colors duration-300 ${
            actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
          }`}>
            Choose accommodations for each location in your safari
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {Array.from(locationsMap.entries()).map(([key, { tour, providers }]) => {
          const location = tour.location?.name || 'Unknown Location';
          const locationKey = getLocationKey(tour.id, location);
          const isExpanded = expandedLocations.has(locationKey);
          const selection = getSelectionForLocation(tour.id, location);
          const isLoading = loading[tour.id];

          return (
            <div
              key={key}
              className={`rounded-xl border overflow-hidden transition-all duration-300 ${
                actualTheme === 'dark'
                  ? 'bg-slate-800/50 border-slate-700'
                  : 'bg-white border-neutral-200'
              } hover:shadow-lg`}
            >
              {/* Location Header */}
              <button
                onClick={() => toggleLocation(tour.id, location)}
                className={`w-full px-6 py-4 flex items-center justify-between transition-colors duration-200 ${
                  actualTheme === 'dark'
                    ? 'hover:bg-slate-700/50'
                    : 'hover:bg-neutral-50'
                }`}
              >
                <div className="flex items-center gap-4 flex-1 text-left">
                  <div className={`p-2 rounded-lg ${
                    actualTheme === 'dark' ? 'bg-slate-700' : 'bg-neutral-100'
                  }`}>
                    <MapPinIcon className={`w-5 h-5 ${
                      actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>
                      {location}
                    </h4>
                    <p className={`text-sm transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                    }`}>
                      {tour.title} • {providers.length} accommodation{providers.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                  {selection && (
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      actualTheme === 'dark'
                        ? 'bg-green-900/30 text-green-300'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      Selected
                    </div>
                  )}
                </div>
                <div className={`ml-4 transform transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Accommodation Options */}
              {isExpanded && (
                <div className={`px-6 py-4 border-t transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'border-slate-700 bg-slate-800/30' : 'border-neutral-200 bg-neutral-50/50'
                }`}>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
                    </div>
                  ) : providers.length === 0 ? (
                    <div className="text-center py-8">
                      <BuildingOfficeIcon className={`w-12 h-12 mx-auto mb-3 ${
                        actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'
                      }`} />
                      <p className={`text-sm transition-colors duration-300 ${
                        actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                      }`}>
                        No accommodations available for this location
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Let SightTrails Choose Option */}
                      <div
                        onClick={() => {
                          if (selection?.providerId) {
                            removeSelection(tour.id, location);
                          } else {
                            updateSelection(tour.id, location, {
                              providerId: undefined,
                              nights: 1
                            });
                          }
                        }}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                          !selection?.providerId
                            ? actualTheme === 'dark'
                              ? 'border-primary-500 bg-primary-900/20'
                              : 'border-primary-500 bg-primary-50'
                            : actualTheme === 'dark'
                            ? 'border-slate-600 hover:border-slate-500'
                            : 'border-neutral-300 hover:border-neutral-400'
                        } hover:shadow-md`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            !selection?.providerId
                              ? actualTheme === 'dark' ? 'bg-primary-700' : 'bg-primary-100'
                              : actualTheme === 'dark' ? 'bg-slate-700' : 'bg-neutral-200'
                          }`}>
                            <SparklesIcon className={`w-5 h-5 ${
                              !selection?.providerId
                                ? 'text-primary-300'
                                : actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className={`font-semibold transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                              Let SightTrails Choose
                            </div>
                            <div className={`text-sm transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                              We'll select the best accommodation based on your preferences
                            </div>
                          </div>
                          {!selection?.providerId && (
                            <CheckIcon className={`w-6 h-6 ${
                              actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                            }`} />
                          )}
                        </div>
                      </div>

                      {/* Provider Options */}
                      {providers.map((provider) => {
                        const isSelected = selection?.providerId === provider.id;
                        const nights = selection?.nights || 1;
                        const mealPlan = selection?.mealPlan;
                        const price = calculatePrice(provider, nights, mealPlan);

                        return (
                          <div
                            key={provider.id}
                            onClick={() => {
                              updateSelection(tour.id, location, {
                                providerId: provider.id,
                                roomType: undefined,
                                mealPlan: undefined,
                                nights: selection?.nights || 1
                              });
                            }}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? actualTheme === 'dark'
                                  ? 'border-primary-500 bg-primary-900/20'
                                  : 'border-primary-500 bg-primary-50'
                                : actualTheme === 'dark'
                                ? 'border-slate-600 hover:border-slate-500'
                                : 'border-neutral-300 hover:border-neutral-400'
                            } hover:shadow-md`}
                          >
                            <div className="flex items-start gap-4">
                              <div className={`p-3 rounded-lg flex-shrink-0 ${
                                isSelected
                                  ? actualTheme === 'dark' ? 'bg-primary-700' : 'bg-primary-100'
                                  : actualTheme === 'dark' ? 'bg-slate-700' : 'bg-neutral-200'
                              }`}>
                                <BuildingOfficeIcon className={`w-6 h-6 ${
                                  isSelected
                                    ? actualTheme === 'dark' ? 'text-primary-300' : 'text-primary-600'
                                    : actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4 mb-2">
                                  <div className="flex-1">
                                    <div className={`font-semibold mb-1 transition-colors duration-300 ${
                                      actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                                    }`}>
                                      {provider.name}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        getTypeColor(provider.type)
                                      }`}>
                                        {provider.type}
                                      </span>
                                      {provider.maxGuests && (
                                        <span className={`inline-flex items-center gap-1 text-xs ${
                                          actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                                        }`}>
                                          <UsersIcon className="w-3 h-3" />
                                          Up to {provider.maxGuests} guests
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <CheckIcon className={`w-6 h-6 flex-shrink-0 ${
                                      actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                                    }`} />
                                  )}
                                </div>

                                {provider.amenities && provider.amenities.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-3">
                                    {provider.amenities.slice(0, 3).map((amenity, idx) => (
                                      <span
                                        key={idx}
                                        className={`px-2 py-0.5 rounded text-xs ${
                                          actualTheme === 'dark'
                                            ? 'bg-slate-700 text-slate-300'
                                            : 'bg-neutral-200 text-neutral-700'
                                        }`}
                                      >
                                        {amenity}
                                      </span>
                                    ))}
                                    {provider.amenities.length > 3 && (
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        actualTheme === 'dark'
                                          ? 'bg-slate-700 text-slate-300'
                                          : 'bg-neutral-200 text-neutral-700'
                                      }`}>
                                        +{provider.amenities.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Selection Details */}
                                {isSelected && (
                                  <div className={`mt-3 pt-3 border-t space-y-3 ${
                                    actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                                  }`}>
                                    {/* Nights Selection */}
                                    <div>
                                      <label className={`block text-sm font-medium mb-2 ${
                                        actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                                      }`}>
                                        Number of Nights
                                      </label>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (nights > 1) {
                                              updateSelection(tour.id, location, { nights: nights - 1 });
                                            }
                                          }}
                                          className={`p-1.5 rounded-lg transition-colors ${
                                            actualTheme === 'dark'
                                              ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                              : 'bg-neutral-200 hover:bg-neutral-300 text-neutral-700'
                                          }`}
                                        >
                                          <XMarkIcon className="w-4 h-4" />
                                        </button>
                                        <input
                                          type="number"
                                          value={nights}
                                          onChange={(e) => {
                                            const value = Math.max(1, parseInt(e.target.value) || 1);
                                            updateSelection(tour.id, location, { nights: value });
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          min="1"
                                          className={`w-20 px-3 py-1.5 text-center border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${
                                            actualTheme === 'dark'
                                              ? 'bg-slate-700 border-slate-600 text-slate-200'
                                              : 'bg-white border-gray-300 text-gray-900'
                                          }`}
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateSelection(tour.id, location, { nights: nights + 1 });
                                          }}
                                          className={`p-1.5 rounded-lg transition-colors ${
                                            actualTheme === 'dark'
                                              ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                              : 'bg-neutral-200 hover:bg-neutral-300 text-neutral-700'
                                          }`}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>

                                    {/* Meal Plan Selection */}
                                    {provider.mealPlans && (
                                      <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                                        }`}>
                                          Meal Plan (Optional)
                                        </label>
                                        <select
                                          value={mealPlan || 'none'}
                                          onChange={(e) => {
                                            updateSelection(tour.id, location, {
                                              mealPlan: e.target.value === 'none' ? undefined : e.target.value as any
                                            });
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${
                                            actualTheme === 'dark'
                                              ? 'bg-slate-700 border-slate-600 text-slate-200'
                                              : 'bg-white border-gray-300 text-gray-900'
                                          }`}
                                        >
                                          <option value="none">No meals</option>
                                          {provider.mealPlans.breakfast && (
                                            <option value="breakfast">Breakfast (+{provider.currency} {provider.mealPlans.breakfast}/night)</option>
                                          )}
                                          {provider.mealPlans.halfBoard && (
                                            <option value="halfBoard">Half Board (+{provider.currency} {provider.mealPlans.halfBoard}/night)</option>
                                          )}
                                          {provider.mealPlans.fullBoard && (
                                            <option value="fullBoard">Full Board (+{provider.currency} {provider.mealPlans.fullBoard}/night)</option>
                                          )}
                                        </select>
                                      </div>
                                    )}

                                    {/* Price Display */}
                                    <div className={`p-3 rounded-lg ${
                                      actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-neutral-100'
                                    }`}>
                                      <div className="flex items-center justify-between">
                                        <span className={`text-sm transition-colors duration-300 ${
                                          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                                        }`}>
                                          Total for {nights} night{nights !== 1 ? 's' : ''}
                                        </span>
                                        <span className={`text-lg font-bold transition-colors duration-300 ${
                                          actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                                        }`}>
                                          {provider.currency} {price.toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Price Preview (when not selected) */}
                                {!isSelected && (
                                  <div className={`mt-2 flex items-center gap-2 text-sm ${
                                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                                  }`}>
                                    <CurrencyDollarIcon className="w-4 h-4" />
                                    <span>
                                      From {provider.currency} {(provider.basePricePerNight || 0).toLocaleString()} per night
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {selections.length > 0 && (
        <div className={`rounded-xl border p-6 transition-all duration-300 ${
          actualTheme === 'dark'
            ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
            : 'bg-white/90 border-neutral-200/50'
        }`}>
          <h4 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
          }`}>
            Accommodation Summary
          </h4>
          <div className="space-y-3">
            {selections.map((selection, index) => {
              const tour = tours.find(t => t.id === selection.tourId);
              const provider = selection.providerId
                ? Object.values(providersByTour).flat().find(p => p.id === selection.providerId)
                : null;

              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-neutral-100'
                  }`}
                >
                  <div className="flex-1">
                    <div className={`font-medium transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                    }`}>
                      {selection.location}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                    }`}>
                      {provider ? provider.name : 'Let SightTrails Choose'} • {selection.nights} night{selection.nights !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {provider && (
                    <div className={`text-right font-semibold transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                    }`}>
                      {provider.currency} {calculatePrice(provider, selection.nights, selection.mealPlan).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationSelector;

